import React, { useEffect, useState, useCallback } from 'react';
import Form from '../components/Form';
import FormV2 from '../components/FormV2';
import { validateForm, validateFormV2 } from '../utils/validation';
import { buildV1FormData, buildV2FormData } from '../utils/buildFormData';
import useTaskPolling from '../hooks/useTaskPolling';

const INITIAL_FORM_STATE = {
    params: {
        buyerId: '',
        current_startDate: null,
        current_endDate: null,
        previous_startDate: null,
        previous_endDate: null,
        channelIds: [],
        groupedChannelIds: {},
        selectedChannelIds: [],
        selectedChannels: [],
        channels_with_names: [],
        setSelectedButtons: [],
        reportType: 'bad',
        // V2 params
        identifierColumn: '',
        fundedColumn: '',
        availableFundedValues: [],
        selectedFundedValues: [],
        existingFiles: [],
        selectedExistingFile: '',
        fileLabel: '',
        uploadFile: null,
        fileData: null,
    },
    errors: {
        buyerId: '',
        setSelectedButtons: [],
        selectedChannels: [],
        current_startDate: null,
        current_endDate: null,
        previous_startDate: null,
        previous_endDate: null,
        // V2 field errors
        uploadFile: '',
        identifierColumn: '',
        fundedColumn: '',
    },
};

const ENDPOINTS = {
    v1: '/api/conversion-analytics',
    v2: '/api/conversion-analytics-v2',
};

const VALIDATORS = {
    v1: validateForm,
    v2: validateFormV2,
};

const FORM_DATA_BUILDERS = {
    v1: buildV1FormData,
    v2: buildV2FormData,
};

const DataAnalytics = () => {
    const [version, setVersion] = useState('v1');
    const [formStateV1, setFormStateV1] = useState(INITIAL_FORM_STATE);
    const [formStateV2, setFormStateV2] = useState(INITIAL_FORM_STATE);

    const formState = version === 'v1' ? formStateV1 : formStateV2;
    const setFormState = version === 'v1' ? setFormStateV1 : setFormStateV2;

    const resetForm = useCallback(() => {
        setFormStateV1(INITIAL_FORM_STATE);
        setFormStateV2(INITIAL_FORM_STATE);
    }, []);

    const { submitTask, loading, message, messageType } =
        useTaskPolling(resetForm);

    const handleFormSubmit = useCallback(async () => {
        if (loading) return;

        const validate = VALIDATORS[version];
        const validationErrors = validate(formState);

        if (Object.keys(validationErrors).length > 0) {
            setFormState(prev => ({
                ...prev,
                errors: { ...prev.errors, ...validationErrors },
            }));
            return;
        }

        const buildPayload = FORM_DATA_BUILDERS[version];
        const formData = buildPayload(formState.params);
        await submitTask(ENDPOINTS[version], formData);
    }, [formState, loading, setFormState, submitTask, version]);

    useEffect(() => {
        document.title = 'Conversion Analytics';
    }, []);

    return (
        <div className={`container${version === 'v2' ? ' container-v2' : ''}`}>
            <h1>DOT818 ANALYSIS</h1>

            <div className="top-controls">
                <button
                    className={`view-button ${version === 'v1' ? 'active' : ''}`}
                    onClick={() => setVersion('v1')}
                    disabled={loading}
                >
                    Version 1
                </button>
                <button
                    className={`view-button ${version === 'v2' ? 'active' : ''}`}
                    onClick={() => setVersion('v2')}
                    disabled={loading}
                >
                    Version 2
                </button>
            </div>

            {version === 'v1' ? (
                <Form
                    formState={formStateV1}
                    setFormState={setFormStateV1}
                    disabled={loading}
                />
            ) : (
                <FormV2
                    formState={formStateV2}
                    setFormState={setFormStateV2}
                    disabled={loading}
                />
            )}

            <div className="accept-button">
                <button
                    onClick={handleFormSubmit}
                    className="filter-btn"
                    disabled={loading}
                >
                    {loading ? (
                        <span className="btn-content">
                            <span className="btn-spinner"></span> Generating...
                        </span>
                    ) : (
                        'Accept'
                    )}
                </button>
            </div>

            <div
                className={`response-message ${messageType} ${message ? 'show' : ''}`}
            >
                {message && <p>{message}</p>}
            </div>
        </div>
    );
};

export default DataAnalytics;
